import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import SweetAlert from "react-bootstrap-sweetalert";
import {
  createMilestoneApi,
  getMilestoneByIdApi,
  updateMilestoneApi,
} from "../../Constants/api";
import { uploadFile } from "../../../Blogs/others/Uploads/upload.Helper";
import { creatingStates } from "../../../Blogs/Constants";
import UploadBlogsStatus from "../../../../../../Components/UploadBlogsStatus";
import { getImageUrl } from "../../utils/imageLoader";

const CreateMilestone = () => {
  const history = useHistory();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    points: "",
    days: "",
    minutes: "",
    imageURL: "",
  });

  const [imageFile, setImageFile] = useState(null);
  const [previewImage, setPreviewImage] = useState("");
  const [thumbnailUploadPercentage, setThumbnailUploadPercentage] = useState(0);
  const [creating, setCreating] = useState({
    isCreating: false,
    creatingStage: creatingStates.UPLOADING_IMAGE,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [errors, setErrors] = useState({});

  const { title, description, points, days, minutes, imageURL } = formData;
  const { isCreating, creatingStage } = creating;

  useEffect(() => {
    if (id) {
      fetchMilestoneDetails(id);
    }
  }, [id]);

  const fetchMilestoneDetails = async (milestoneId) => {
    try {
      const { data } = await getMilestoneByIdApi(milestoneId);
      if (data && data.data) {
        setFormData({
          title: data.data.title || "",
          description: data.data.description || "",
          points: data.data.points || "",
          days: data.data.days || "",
          minutes: data.data.minutes || "",
          imageURL: data.data.imageURL || "",
        });

        // Convert SVG to data URL if needed
        if (data.data.imageURL) {
          const imageUrl = await getImageUrl(data.data.imageURL);
          setPreviewImage(imageUrl || data.data.imageURL);
        } else {
          setPreviewImage("");
        }
      }
    } catch (error) {
      console.error("Error fetching milestone details:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Validation for points, days and minutes to only allow numbers
    if (name === "points" || name === "days" || name === "minutes") {
      if (value !== "" && !/^\d*$/.test(value)) {
        return;
      }
    }

    setFormData({
      ...formData,
      [name]: value,
    });

    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Allowed file types
      const allowedFormats = ['image/jpeg', 'image/png'];
      const allowedExtensions = ['.jpg', '.jpeg', '.png'];

      // Check file type
      const fileType = file.type.toLowerCase();
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

      const isValidType = allowedFormats.includes(fileType);
      const isValidExtension = allowedExtensions.includes(fileExtension);

      if (!isValidType || !isValidExtension) {
        setErrors({
          ...errors,
          imageURL: "Invalid file format. Only JPG, JPEG, and PNG formats are accepted."
        });
        setImageFile(null);
        setPreviewImage("");
        e.target.value = ""; // Reset file input
        return;
      }

      // File is valid
      setImageFile(file);
      setPreviewImage(URL.createObjectURL(file));
      if (errors.imageURL) {
        setErrors({ ...errors, imageURL: null });
      }
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!title?.trim()) newErrors.title = "Title is required";
    if (!description?.trim()) newErrors.description = "Description is required";
    if (points === "" || points === undefined) newErrors.points = "Points are required";
    if (days === "" || days === undefined) newErrors.days = "Days are required";
    if (minutes === "" || minutes === undefined) newErrors.minutes = "Minutes are required";

    if (!id && !imageFile) newErrors.imageURL = "Image is required";
    if (id && !imageFile && !imageURL) newErrors.imageURL = "Image is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsLoading(true);

    setCreating({
      isCreating: true,
      creatingStage: creatingStates.UPLOADING_IMAGE,
    });

    try {
      let uploadedImageUrl = imageURL;
      if (imageFile) {
        uploadedImageUrl = await uploadFile(
          imageFile,
          "MILESTONE-IMAGE",
          setThumbnailUploadPercentage,
          20
        );
      }

      setCreating({
        isCreating: true,
        creatingStage: creatingStates.CREATING_BLOG,
      });

      const payload = {
        title,
        description,
        points: Number(points),
        days: Number(days),
        minutes: Number(minutes),
        imageURL: uploadedImageUrl,
      };

      if (id) {
        await updateMilestoneApi(id, payload);
      } else {
        await createMilestoneApi(payload);
      }

      setAlert(
        <SweetAlert
          success
          title={id ? "Updated!" : "Created!"}
          onConfirm={() => {
            setAlert(null);
            history.push("/dashboard/milestones/all");
          }}
        >
          Milestone has been {id ? "updated" : "created"} successfully.
        </SweetAlert>
      );

      setCreating({
        isCreating: true,
        creatingStage: creatingStates.UPLOAD_COMPLETE,
      });

    } catch (error) {
      console.error("Error submitting form:", error);
      setCreating({
        isCreating: false,
        creatingStage: creatingStates.UPLOADING_IMAGE,
      });
      setAlert(
        <SweetAlert
          danger
          title="Error"
          onConfirm={() => setAlert(null)}
        >
          Something went wrong.
        </SweetAlert>
      );
    } finally {
      setIsLoading(false);
    }
  };

  const reset = () => {
    setCreating({ isCreating: false, creatingStage: creatingStates.UPLOADING_IMAGE });
    setThumbnailUploadPercentage(0);
    setFormData({
      title: "",
      description: "",
      points: "",
      days: "",
      minutes: "",
      imageURL: "",
    });
    setImageFile(null);
    setPreviewImage("");
    setAlert(null);
  };

  const ErrorMsg = ({ msg }) => (
    msg ? <div style={{ color: "red", fontSize: "12px", marginTop: "5px" }}>{msg}</div> : null
  );

  return (
    <>
      {isCreating ? (
        <UploadBlogsStatus
          currentStep={creatingStage}
          thumbnailUploadPercentage={thumbnailUploadPercentage}
          audioUploadPercentage={0}
          reset={reset}
          fileName="milestones"
        />
      ) : (
        <div className="card">
          <div className="card-header">
            <h3>{id ? "Edit Milestone" : "Create Milestone"}</h3>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Title <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className="form-control"
                  name="title"
                  value={title}
                  onChange={handleChange}
                  disabled={isLoading}
                  placeholder="Enter Title"
                />
                <ErrorMsg msg={errors.title} />
              </div>

              <div className="form-group">
                <label>Description <span className="text-danger">*</span></label>
                <textarea
                  className="form-control"
                  name="description"
                  value={description}
                  onChange={handleChange}
                  rows="4"
                  disabled={isLoading}
                  placeholder="Enter Description"
                />
                <ErrorMsg msg={errors.description} />
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Points <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      name="points"
                      value={points}
                      onChange={handleChange}
                      disabled={isLoading}
                      placeholder="Enter Points (Numbers only)"
                    />
                    <ErrorMsg msg={errors.points} />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Days <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      name="days"
                      value={days}
                      onChange={handleChange}
                      disabled={isLoading}
                      placeholder="Enter Days (Numbers only)"
                    />
                    <ErrorMsg msg={errors.days} />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Duration(in minutes) <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      name="minutes"
                      value={minutes}
                      onChange={handleChange}
                      disabled={isLoading}
                      placeholder="Enter minutes (Numbers only)"
                    />
                    <ErrorMsg msg={errors.minutes} />
                  </div>
                </div>
                <div className="col-md-12">
                  <div className="form-group">
                    <label>Milestone Image <span className="text-danger">*</span></label>
                    <small className="d-block mb-2" style={{ color: "#666" }}>
                      <strong>Accepted formats:</strong> JPG, JPEG, PNG
                    </small>
                    <input
                      type="file"
                      className="form-control"
                      onChange={handleImageChange}
                      accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                      disabled={isLoading}
                    />
                    <ErrorMsg msg={errors.imageURL} />
                    {previewImage && (
                      <div className="mt-3">
                        <img
                          src={previewImage}
                          alt="Milestone Preview"
                          style={{ width: "200px", height: "auto", borderRadius: "8px", border: "1px solid #ddd", maxHeight: "200px" }}
                          crossOrigin="anonymous"
                          onError={(e) => {
                            console.error("Image preview failed to load", e);
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <button type="submit" className="btn btn-primary mt-3" disabled={isLoading}>
                {isLoading ? "Saving..." : (id ? "Update" : "Create")}
              </button>
              {id && (
                <button type="button" className="btn btn-secondary mt-3 ml-2" onClick={() => history.push("/dashboard/milestones/all")} disabled={isLoading}>
                  Cancel
                </button>
              )}
            </form>
          </div>
          {alert}
        </div>
      )}
    </>
  );
};

export default CreateMilestone;
